import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useApi } from "@/core/api";
import { reportKey } from "@/profitability/query";
import { reportsPath } from "@/profitability/report-query";
import type {
  ProfitabilityReport,
  TemplateChatMessage,
  TemplateChatThread,
} from "@/profitability/types";

export const templateChatPath = (businessId: string, reportId: string) =>
  `${reportsPath(businessId, reportId)}/chat`;

const IDLE_CHAT_POLL_MS = 15_000;

export const templateChatKey = (businessId: string, reportId: string) =>
  [...reportKey(businessId), "template-chat", reportId] as const;

export interface TemplateChatResponse {
  thread: TemplateChatThread | null;
  messages: TemplateChatMessage[];
}

export const useTemplateChat = (
  businessId: string,
  reportId: string,
  building: boolean,
  /** Read only where the conversation can be seen or acted on. */
  enabled = true,
) => {
  const api = useApi();
  return useQuery({
    queryKey: templateChatKey(businessId, reportId),
    queryFn: ({ signal }) =>
      api
        .get<TemplateChatResponse>(templateChatPath(businessId, reportId), {
          signal,
        })
        .then((res) => res.data),
    // Read slowly while idle too: a build or turn someone else starts (or this
    // owner, in another tab) has to reach a page that last read it as done.
    enabled,
    refetchInterval: building ? 4_000 : IDLE_CHAT_POLL_MS,
    refetchOnWindowFocus: "always",
    retry: false,
  });
};

export interface TemplateChatRequest {
  message: string;
  document_ids: string[];
  images: File[];
  /** The run the Result tab shows; an agent's first message has none. */
  result_run_id?: string;
}

// Multipart only when there are screenshots to carry.
const chatBody = ({
  message,
  document_ids,
  images,
  result_run_id,
}: TemplateChatRequest) => {
  if (images.length === 0)
    return {
      message,
      document_ids,
      ...(result_run_id ? { result_run_id } : {}),
    };
  const formData = new FormData();
  formData.append("message", message);
  document_ids.forEach((id) => formData.append("document_ids[]", id));
  images.forEach((image) => formData.append("images[]", image, image.name));
  if (result_run_id) formData.append("result_run_id", result_run_id);
  return formData;
};

export const useStartTemplateChat = (businessId: string) => {
  const api = useApi();
  const client = useQueryClient();
  return useMutation({
    mutationFn: (request: TemplateChatRequest) =>
      api
        .post<{
          report: ProfitabilityReport;
          thread: TemplateChatThread;
          turn_id: string;
        }>(`${reportsPath(businessId)}/chat`, chatBody(request))
        .then((res) => res.data),
    onSuccess: () =>
      void client.invalidateQueries({
        queryKey: [...reportKey(businessId), "reports"],
      }),
  });
};

/** Folds the conversation so far into notes the next replies read instead. */
export const useSummarizeTemplateChat = (
  businessId: string,
  reportId: string,
) => {
  const api = useApi();
  return useMutation({
    mutationFn: () =>
      api
        .post<{
          turn_id: string;
          thread: TemplateChatThread;
        }>(`${templateChatPath(businessId, reportId)}/compact`)
        .then((res) => res.data),
  });
};

export const useSendTemplateChat = (businessId: string, reportId: string) => {
  const api = useApi();
  return useMutation({
    mutationFn: (request: TemplateChatRequest) =>
      api
        .post<{
          turn_id: string;
          thread: TemplateChatThread;
        }>(templateChatPath(businessId, reportId), chatBody(request))
        .then((res) => res.data),
  });
};
